import {Button, Dropdown} from 'react-bootstrap-v5'
import {ChatSquareText, FileEarmarkText, Star} from 'react-bootstrap-icons'
import {InterviewSession, PaginatedRecord, ReviewUser, VJFPUser} from 'app/API'
import {getInitials, getStyleByIndex} from 'app/helpers/MiscUtils'
import {useCallback, useEffect, useState} from 'react'
import JobseekerRegistrationsModal from 'app/modules/shared/jobseeker-registrations/JobseekerRegistrationsModal'
import {BasePageProps} from 'app/types'
import {BoothService} from 'app/services/BoothService'
import {InterviewSessionService} from 'app/services/InterviewSessionService'
import {ResumeService} from 'app/services/resumeService'
import {ReviewUserService} from 'app/services/ReviewUserService'
import {VJFPUserService} from 'app/services/VJFPUserService'
import {parse} from 'zipson'
import {useAuth} from 'app/context/auth-context'
import {useAppContext} from 'app/context/app-context'
import BlockUi from 'react-block-ui'
import 'react-block-ui/style.css'
import moment from 'moment'
import {getFileUrlFromS3} from 'app/helpers/awsUtils'
import {CandidateProfileForHistory} from './CandidateProfileForHistory'
import saveAs from 'file-saver'
import RecruiterBoothMatrix from 'app/modules/shared/RecruiterBoothMatrix'
import {HMSMessage} from '@100mslive/react-sdk'
import {useS3Object} from 'app/context/S3ObjectContext'
interface InterviewSessionsItem extends InterviewSession {
  candidate?: VJFPUser
  recruiter?: VJFPUser
  reviewUser?: ReviewUser
}

interface ICandidateResumeItem {
  jobSeeker: VJFPUser
  fileName: string
  resumeUrl?: string
  jobTitle?: string | null
  notes?: string | null
}

const RecruiterHistory = (props: BasePageProps) => {
  const {user} = useAuth()
  const {jobFair} = useAppContext()
  const reviewUserService = new ReviewUserService()
  const vjfpUserService = new VJFPUserService()
  const resumeService = new ResumeService()
  const boothService = new BoothService()
  const interviewSessionService = new InterviewSessionService()

  const [interviewSessions, setInterviewSessions] = useState<InterviewSessionsItem[]>([])
  const [resumes, setResumes] = useState<ICandidateResumeItem[]>([])
  const [activeInterviewSession, setActiveInterviewSession] = useState<
    InterviewSession | undefined
  >(undefined)
  const [chatRecords, setChatRecords] = useState<HMSMessage[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [selectedCandidate, setSelectedCandidate] = useState<VJFPUser | undefined>(undefined)
  const [lastExecutedKey, setLastExecutedKey] = useState<any>(undefined)
  const [boothId, setBoothId] = useState<string | undefined>(undefined)
  const [recruiters, setRecruiters] = useState<VJFPUser[]>([])
  const [selectedRecruiterId, setSelectedRecruiterId] = useState<number | undefined>(undefined)
  const [boothName, setBoothName] = useState<string | undefined>('')
  const [showApplicantsModal, setShowApplicantsModal] = useState<boolean>(false)
  const [downloadingExcel, setDownloadingExcel] = useState<boolean>(false)
  const [activeTab, setActiveTab] = useState<'conversations' | 'resumes'>('conversations')
  const [shouldAutoSelectFirst, setShouldAutoSelectFirst] = useState<boolean>(false)
  const {getS3ObjectUrl, s3ObjectCache} = useS3Object()

  const getInterviewSession = useCallback(async () => {
    if (boothId && jobFair?.id && user?.id && recruiters?.length) {
      setLoading(true)

      let paginatedInterviewSessions
      if (selectedRecruiterId) {
        paginatedInterviewSessions = (await interviewSessionService.getInterviewsByRecruiter(
          jobFair.id,
          selectedRecruiterId,
          10,
          lastExecutedKey
        )) as PaginatedRecord<InterviewSessionsItem[]>
      } else {
        paginatedInterviewSessions = (await interviewSessionService.getInterviewsByBoothId(
          jobFair.id,
          boothId,
          10,
          lastExecutedKey
        )) as PaginatedRecord<InterviewSessionsItem[]>
      }

      if (paginatedInterviewSessions && paginatedInterviewSessions.items) {
        const newItems = paginatedInterviewSessions.items

        // 1. Collect all IDs for batch fetching
        const candidateIds = newItems
          .map((item) => item.candidateId)
          .filter((id): id is number => !!id)
        // Match key using user.id as per original logic to fetch relevant reviews
        const currentReviewKeys = newItems.map((item) => ({
          jobFairId: jobFair.id,
          interviewSessionId: item.id,
          recruiterId: Number(user.id),
        }))

        // 2. Fetch Candidates and Reviews in Parallel
        const [candidates, reviews] = await Promise.all([
          vjfpUserService.getByIds('00000000-0000-0000-0000-000000000000', candidateIds),
          interviewSessionService.getReviewsBySessionIds(jobFair.id, currentReviewKeys),
        ])

        const candidateMap = new Map(candidates?.map((c) => [c.id, c]))
        const reviewMap = new Map()
        if (reviews) {
          reviews.forEach((r) => {
            const sessionId = r.interviewSession || r.interviewSessionId
            const ratedBy = r.ratedBy
            if (sessionId && ratedBy) {
              reviewMap.set(`${sessionId}#${ratedBy}`, r)
            }
          })
        }

        for (let item of newItems) {
          if (item.candidateId) {
            item.candidate = candidateMap.get(item.candidateId)
          }
          if (item.recruiterId) {
            item.recruiter = recruiters.find((r) => r.id === item.recruiterId)
          }
          // Match key using user.id (convert to string to match DynamoDB format)
          const review = reviewMap.get(`${item.id}#${String(user.id)}`)
          if (review) item.reviewUser = review
        }

        setLastExecutedKey(paginatedInterviewSessions.lastEvaluatedKey)
        setInterviewSessions((prev) => [...prev, ...newItems])

        if (newItems.length > 0 && !activeInterviewSession) {
          setShouldAutoSelectFirst(true)
        }
      }

      setLoading(false)
    }
  }, [jobFair?.id, user?.id, boothId, lastExecutedKey, recruiters?.length])

  useEffect(() => {
    if (!jobFair?.id || !user?.id) return

    boothService.getBoothByRecruiter(jobFair.id, user.id).then((booth) => {
      const ids = []
      if (booth?.recruiters) {
        for (let recruiter of booth.recruiters) {
          if (recruiter) ids.push(recruiter)
        }
      }

      vjfpUserService.getByIds(booth!.organizationId, ids).then((recruiters) => {
        if (recruiters) setRecruiters(recruiters)
      })
      setBoothId(booth?.id)
      setBoothName(booth?.name)
    })
  }, [jobFair?.id, user?.id])

  useEffect(() => {
    if (!jobFair?.id || !user?.id || !boothId || !recruiters?.length) return

    getInterviewSession().then(async () => {
      const getResumesResult = await resumeService.getResumeList(jobFair.id, boothId)
      if (getResumesResult && Array.isArray(getResumesResult)) {
        let resumeArray: ICandidateResumeItem[] = []
        for (let resumeItem of getResumesResult) {
          if (resumeItem) {
            const getDocumentByKeyResult = await getFileUrlFromS3(resumeItem.resumeS3Key)

            resumeArray.push({
              jobSeeker: (await vjfpUserService.getById(
                '00000000-0000-0000-0000-000000000000',
                resumeItem.jobSeekerId
              )) as VJFPUser,
              fileName: resumeItem.fileName,
              resumeUrl: getDocumentByKeyResult.fileUrl,
              jobTitle: resumeItem.jobTitle,
              notes: resumeItem.notes,
            })
          }
        }

        setResumes(resumeArray)
      }
    })
  }, [jobFair?.id, user?.id, boothId, recruiters?.length, selectedRecruiterId])

  const handleRecruiterSelect = (recruiterId: string | null) => {
    setInterviewSessions([])
    setLastExecutedKey(undefined)
    setSelectedRecruiterId(Number(recruiterId))
  }

  const downloadExcel = () => {
    if (jobFair?.id && boothId) {
      setDownloadingExcel(true)
      interviewSessionService
        .getExcelInterviewsByBoothId(jobFair.id, boothId)
        .then((base64Data) => {
          if (base64Data) {
            const byteCharacters = atob(base64Data)
            const byteNumbers = new Array(byteCharacters.length)
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i)
            }
            const byteArray = new Uint8Array(byteNumbers)
            const blob = new Blob([byteArray], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            })
            saveAs(blob, `candidate-ratings-${moment().format('yyyy-MM-DD')}.xlsx`)
          }
        })
        .finally(() => {
          setDownloadingExcel(false)
        })
    }
  }

  useEffect(() => {
    if (activeInterviewSession) {
      if (activeInterviewSession.chatTranscript) {
        setChatRecords(parse(activeInterviewSession.chatTranscript) as HMSMessage[])
      } else {
        setChatRecords([])
      }
    } else {
      setChatRecords([])
    }
  }, [activeInterviewSession])

  useEffect(() => {
    const candidatesToFetch = [
      ...interviewSessions.map((s: InterviewSessionsItem) => s.candidate),
      ...resumes.map((r: ICandidateResumeItem) => r.jobSeeker),
    ].filter((c): c is VJFPUser => !!c && !!c.avatar)

    candidatesToFetch.forEach((c) => {
      if (c.avatar) getS3ObjectUrl(c.avatar)
    })
  }, [interviewSessions, resumes, getS3ObjectUrl])

  // Auto-select first item when switching tabs
  useEffect(() => {
    if (activeTab === 'conversations' && interviewSessions.length > 0 && !selectedCandidate) {
      setSelectedCandidate(interviewSessions[0].candidate)
    } else if (activeTab === 'resumes' && resumes.length > 0 && !selectedCandidate) {
      setSelectedCandidate(resumes[0].jobSeeker)
    }
  }, [activeTab, interviewSessions.length, resumes.length])

  // Auto-select first interview session after new items are loaded
  useEffect(() => {
    if (shouldAutoSelectFirst && interviewSessions.length > 0 && !activeInterviewSession) {
      setShouldAutoSelectFirst(false)
      onActiveInterviewSessionIdChanged(
        interviewSessions[0].jobFairId,
        interviewSessions[0].id,
        interviewSessions[0].candidate
      )
    }
  }, [shouldAutoSelectFirst, interviewSessions, activeInterviewSession])

  const onActiveInterviewSessionIdChanged = (jobFairId: string, id?: string, candidate?: any) => {
    if (candidate) {
      setSelectedCandidate(candidate)
    }

    if (id)
      interviewSessionService.getById(jobFairId, id).then(async (_interviewSession) => {
        if (_interviewSession?.candidateId) {
          setActiveInterviewSession(_interviewSession)
        } else {
          setActiveInterviewSession(undefined)
        }
      })
  }

  return (
    <div
      className='d-flex flex-column h-100 bg-light'
      style={{height: 'calc(100vh - 65px)', overflow: 'hidden'}}
    >
      {/* Sticky Header Section */}
      <div className='bg-white border-bottom shadow-sm z-index-2' style={{zIndex: 99}}>
        <div className='container-fluid py-3'>
          {/* Title & Actions */}
          <div className='d-flex align-items-center justify-content-between mb-4'>
            <h1 className='d-flex align-items-center text-dark fw-bolder my-1 fs-3'>
              {'Report' + (boothName ? ' - ' + boothName : '')}
            </h1>
            <div className='d-flex gap-2'>
              <Button
                variant='light-primary'
                className='fw-bold btn-active-light-primary'
                onClick={() => setShowApplicantsModal(true)}
              >
                <i className='fas fa-users me-2'></i> Applicants
              </Button>

              <Button
                variant='primary'
                className='fw-bold'
                onClick={downloadExcel}
                disabled={downloadingExcel}
              >
                {downloadingExcel ? (
                  <>
                    <span
                      className='spinner-border spinner-border-sm me-2'
                      role='status'
                      aria-hidden='true'
                    ></span>
                    Exporting...
                  </>
                ) : (
                  <>
                    <i className='fas fa-download me-2'></i> Report
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Stats Matrix using RecruiterBoothMatrix */}
          {boothId && <RecruiterBoothMatrix boothId={boothId} />}

          {/* Filters Row */}
          <div className='d-flex align-items-center gap-2 mt-4'>
            <label className='text-muted fw-bold fs-7 mb-0 me-2'>Filter by Recruiter:</label>
            <Dropdown onSelect={handleRecruiterSelect} className='d-inline-block'>
              <Dropdown.Toggle
                variant='white'
                id='dropdown-basic'
                className='btn btn-sm btn-outline btn-outline-dashed btn-outline-default d-flex align-items-center bg-white'
              >
                {selectedRecruiterId
                  ? recruiters.find((r) => r.id === selectedRecruiterId)?.firstName
                  : 'All Recruiters'}
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item eventKey=''>All Recruiters</Dropdown.Item>
                <Dropdown.Divider />
                {recruiters.map((recruiter) => (
                  <Dropdown.Item key={recruiter.id} eventKey={recruiter.id.toString()}>
                    {recruiter.firstName} {recruiter.lastName}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </div>

      {/* Main Content Container - Fills remaining height */}
      <div className='container-fluid d-flex flex-column flex-grow-1 overflow-hidden p-0'>
        {/* 2. Split View Content */}
        <div className='row g-0 h-100'>
          {/* Left Panel: List - Independent Scroll */}
          <div className='col-md-4 border-end h-100 d-flex flex-column bg-white'>
            {/* Tabs */}
            <ul
              className='nav nav-tabs nav-line-tabs nav-line-tabs-2x border-transparent fs-5 fw-bolder px-3 pt-3 bg-white sticky-top z-index-1'
              role='tablist'
            >
              <li className='nav-item' role='presentation'>
                <button
                  className={`nav-link text-active-primary ms-0 me-10 py-5 ${
                    activeTab === 'conversations' ? 'active' : ''
                  }`}
                  onClick={() => setActiveTab('conversations')}
                  type='button'
                  role='tab'
                >
                  <ChatSquareText size={18} className='me-2' />
                  Conversations
                </button>
              </li>
              <li className='nav-item' role='presentation'>
                <button
                  className={`nav-link text-active-primary ms-0 me-10 py-5 ${
                    activeTab === 'resumes' ? 'active' : ''
                  }`}
                  onClick={() => setActiveTab('resumes')}
                  type='button'
                  role='tab'
                >
                  <FileEarmarkText size={18} className='me-2' />
                  Resumes (left at booth)
                </button>
              </li>
            </ul>

            <div className='p-3 flex-grow-1 overflow-auto' id='history-list-container'>
              {activeTab === 'conversations' ? (
                <>
                  {!loading && interviewSessions && interviewSessions.length === 0 ? (
                    <div className='d-flex flex-column align-items-center justify-content-center text-muted p-10 mt-5'>
                      <ChatSquareText size={40} className='mb-3 text-gray-400' />
                      <h5 className='text-gray-600'>No Conversations</h5>
                      <p className='small text-gray-400'>
                        Select a recruiter or wait for candidates.
                      </p>
                    </div>
                  ) : (
                    <BlockUi tag='div' blocking={loading}>
                      <div className='d-flex flex-column gap-3'>
                        {interviewSessions.map((session, index) => {
                          const isSelected = activeInterviewSession?.id === session.id
                          return (
                            <div
                              key={index}
                              className={`card cursor-pointer ${
                                isSelected
                                  ? 'border-primary shadow-sm bg-light-primary'
                                  : 'border border-dashed bg-light-white'
                              }`}
                              onClick={() =>
                                onActiveInterviewSessionIdChanged(
                                  session.jobFairId,
                                  session.id,
                                  session.candidate
                                )
                              }
                              style={{cursor: 'pointer', transition: 'all 0.2s'}}
                            >
                              <div className='card-body p-4'>
                                <div className='d-flex align-items-start'>
                                  {session.candidate?.avatar &&
                                  s3ObjectCache[session.candidate.avatar] ? (
                                    <div className='symbol symbol-40px me-3'>
                                      <img
                                        src={s3ObjectCache[session.candidate.avatar]}
                                        crossOrigin='anonymous'
                                        alt={session.candidate.firstName}
                                      />
                                    </div>
                                  ) : (
                                    <div className='symbol symbol-40px me-3'>
                                      <div
                                        className={`symbol-label fs-5 fw-bold bg-${getStyleByIndex(
                                          index
                                        )} text-inverse-${getStyleByIndex(index)}`}
                                      >
                                        {getInitials(
                                          session.candidate?.firstName,
                                          session.candidate?.lastName
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  <div className='flex-grow-1'>
                                    <div className='d-flex justify-content-between align-items-start mb-1'>
                                      <h6 className='mb-0 fw-bolder text-gray-800'>
                                        {session.candidate?.firstName} {session.candidate?.lastName}
                                      </h6>
                                      {session.reviewUser?.rating && (
                                        <div className='d-flex align-items-center bg-light-warning rounded px-2 py-1'>
                                          <Star
                                            size={12}
                                            className='text-warning me-1'
                                            fill='currentColor'
                                          />
                                          <span className='small fw-bold text-warning-inverse'>
                                            {session.reviewUser.rating}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                    <p
                                      className='small text-gray-600 mb-2'
                                      style={{
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                      }}
                                    >
                                      {session.reviewUser?.notes || (
                                        <span className='text-muted fst-italic'>
                                          No notes available
                                        </span>
                                      )}
                                    </p>
                                    <div className='d-flex justify-content-between align-items-center mt-2'>
                                      <span className='badge badge-light fw-bold text-gray-600'>
                                        <i className='fas fa-user-tie me-1 text-gray-400'></i>{' '}
                                        {session.recruiter?.firstName} {session.recruiter?.lastName}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>

                      {lastExecutedKey && (
                        <div className='p-4 text-center'>
                          <Button
                            variant='light-primary'
                            size='sm'
                            className='w-100 fw-bold'
                            onClick={getInterviewSession}
                          >
                            {loading ? 'Loading...' : 'Load More Interviews'}
                          </Button>
                        </div>
                      )}
                    </BlockUi>
                  )}
                </>
              ) : (
                <>
                  {resumes && resumes.length === 0 ? (
                    <div className='d-flex flex-column align-items-center justify-content-center text-muted p-10 mt-5'>
                      <FileEarmarkText size={40} className='mb-3 text-gray-400' />
                      <p className='text-gray-600'>No Resumes found</p>
                    </div>
                  ) : (
                    <div className='d-flex flex-column gap-3'>
                      {resumes.map((resume, index) => (
                        <div
                          key={index}
                          className='card border border-dashed mb-0 cursor-pointer hover-elevate-up'
                          style={{cursor: 'pointer'}}
                          onClick={() =>
                            onActiveInterviewSessionIdChanged(
                              jobFair?.id || '',
                              undefined,
                              resume.jobSeeker
                            )
                          }
                        >
                          <div className='card-body p-4'>
                            <div className='d-flex align-items-start'>
                              {resume.jobSeeker?.avatar &&
                              s3ObjectCache[resume.jobSeeker.avatar] ? (
                                <div className='symbol symbol-40px me-3'>
                                  <img
                                    src={s3ObjectCache[resume.jobSeeker.avatar]}
                                    crossOrigin='anonymous'
                                    alt={resume.jobSeeker.firstName}
                                  />
                                </div>
                              ) : (
                                <div className='symbol symbol-40px me-3'>
                                  <div
                                    className={`symbol-label fs-5 fw-bold bg-${getStyleByIndex(
                                      index
                                    )} text-inverse-${getStyleByIndex(index)}`}
                                  >
                                    {getInitials(
                                      resume.jobSeeker?.firstName,
                                      resume.jobSeeker?.lastName
                                    )}
                                  </div>
                                </div>
                              )}

                              <div className='flex-grow-1'>
                                <h6 className='mb-1 text-gray-800 fw-bolder'>
                                  {resume.jobSeeker?.firstName} {resume.jobSeeker?.lastName}
                                </h6>
                                <p className='text-muted small mb-2 fw-bold'>
                                  {resume.jobTitle ||
                                    resume.jobSeeker?.designation ||
                                    'No Job Title'}
                                </p>
                                <p className='small text-gray-600 mb-3'>
                                  {resume.notes || (
                                    <span className='text-muted fst-italic'>No notes</span>
                                  )}
                                </p>
                                <div className='d-flex justify-content-between align-items-center'>
                                  <a
                                    href={resume.resumeUrl}
                                    className='btn btn-sm btn-light-primary fw-bold'
                                    download
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <i className='fas fa-download me-1'></i> Download PDF
                                  </a>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Right Panel: Details - Independent Scroll */}
          <div className='col-md-8 h-100 overflow-auto bg-gray-100'>
            {selectedCandidate ? (
              <div className='p-4 p-lg-8 mx-auto' style={{maxWidth: '1200px'}}>
                <CandidateProfileForHistory
                  candidate={selectedCandidate}
                  chatRecords={chatRecords}
                  score={activeInterviewSession?.candidateScore}
                  maxScore={activeInterviewSession?.candidateMaxScore}
                />
              </div>
            ) : (
              <div className='h-100 d-flex flex-column align-items-center justify-content-center text-gray-400'>
                <i className='fas fa-id-card fa-4x mb-4 text-gray-300'></i>
                <h5 className='text-gray-500 fw-bold'>Select a Candidate</h5>
                <p className='text-gray-400'>Click on a conversation or resume to view details.</p>
              </div>
            )}
          </div>
        </div>

        {user && (
          <JobseekerRegistrationsModal
            show={showApplicantsModal}
            onHide={() => setShowApplicantsModal(false)}
            organizationId={user.organizationId}
          />
        )}
      </div>
    </div>
  )
}

export default RecruiterHistory
